"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pointsToBezierCurve = void 0;
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const turf_1 = require("@turf/turf");
/**
 * Convert an array of points to a bezier curved line string
 *
 * @param points The array of points to convert to a bezier curve. There must be at least 2 points, otherwise an exception will be thrown
 * @param options The options to use when converting the line to a bezier curve
 * @param { number } options.superposedSequence The superposed sequence to use when converting the line to a bezier curve. The higher this number, the wider will be the curve
 * @param { object } options.additionalProperties The additional properties to add to the bezier curve feature
 * @returns {GeoJSON.Feature<GeoJSON.LineString>} A GeoJSON LineString feature representing the bezier curve.
 */
const pointsToBezierCurve = (points, { superposedSequence = 0, additionalProperties = {} }) => {
    if (points.length < 2) {
        throw new Error('There must be at least 2 points to convert to a bezier curve');
    }
    const tripCurveCoordinates = [];
    for (let i = 0; i < points.length - 1; i++) {
        const originGeojson = points[i];
        const destinationGeojson = points[i + 1];
        const midpoint = (0, turf_1.midpoint)(originGeojson.coordinates, destinationGeojson.coordinates);
        const distance = (0, turf_1.distance)(originGeojson.coordinates, destinationGeojson.coordinates, { units: 'meters' });
        const bearing = (0, turf_1.bearing)(originGeojson.coordinates, destinationGeojson.coordinates);
        const bezierBearing = bearing + 90;
        const offsetMidPoint = (0, turf_1.destination)(midpoint, ((0.1 + superposedSequence * 0.025) * distance) / 1000, bezierBearing);
        const bezierLine = (0, turf_1.lineString)([
            originGeojson.coordinates,
            offsetMidPoint.geometry.coordinates,
            destinationGeojson.coordinates
        ]);
        const bezierCurve = (0, turf_1.bezierSpline)(bezierLine, { resolution: 10000, sharpness: 1.5 });
        tripCurveCoordinates.push(...bezierCurve.geometry.coordinates);
    }
    return {
        type: 'Feature',
        properties: additionalProperties,
        geometry: {
            type: 'LineString',
            coordinates: tripCurveCoordinates
        }
    };
};
exports.pointsToBezierCurve = pointsToBezierCurve;
//# sourceMappingURL=SurveyGeographyUtils.js.map